/**
 * Configuración de métodos de pago disponibles
 *
 * Aquí puedes activar/desactivar métodos de pago a medida que confirmes
 * contratos y APIs con cada proveedor.
 */

export type PaymentMethodType = 'getnet' | 'khipu' | 'transbank'

export interface PaymentMethodConfig {
  id: PaymentMethodType
  name: string
  description: string
  icon: string
  color: string
  borderColor: string
  backgroundColor: string
  logo?: string
  features: string[]
  securityInfo: string
  enabled: boolean
  inDevelopment?: boolean
  notes?: string
}

export const PAYMENT_METHODS: Record<PaymentMethodType, PaymentMethodConfig> = {
  getnet: {
    id: 'getnet',
    name: '💳 Tarjeta de Crédito / Débito',
    description: 'Visa, Mastercard, American Express',
    icon: '💳',
    color: '#1e40af',
    borderColor: '#1e40af',
    backgroundColor: '#dbeafe',
    logo: 'https://banco.santander.cl/uploads/000/029/870/0620f532-9fc9-4248-b99e-78bae9f13e1d/original/Logo_WebCheckout_Getnet.svg',
    features: [
      '✓ Pago inmediato',
      '✓ Seguro con SSL',
      '✓ Confirmación al instante'
    ],
    securityInfo: `Tarjeta de Crédito/Débito: Procesado directamente por Getnet/Santander
      con encriptación SSL de 256 bits. No almacenamos información de tarjetas. PCI-DSS certificado.`,
    enabled: false,  // Se actualiza dinámicamente desde el backend
    notes: 'Método de pago principal. Completamente funcional en producción.'
  },

  khipu: {
    id: 'khipu',
    name: '🏦 Transferencia Bancaria (Khipu)',
    description: 'Desde cualquier banco - Tiempo real',
    icon: '🏦',
    color: '#7c3aed',
    borderColor: '#7c3aed',
    backgroundColor: '#ede9fe',
    logo: 'https://s3.amazonaws.com/static.khipu.com/buttons/2024/200x75-purple.svg',
    features: [
      '✓ Sin comisiones',
      '✓ Disponible 24/7',
      '✓ Seguro certificado'
    ],
    securityInfo: `Transferencia Bancaria (Khipu): Realizada directamente desde tu banco
      a través de Khipu. Dinero en cuenta en tiempo real, sin intermediarios. Disponible 24/7 todos los días.`,
    enabled: false,  // Se actualiza dinámicamente desde el backend
    notes: 'Contrato confirmado. Completamente funcional en producción.'
  },

  transbank: {
    id: 'transbank',
    name: '💳 Webpay Plus (Transbank)',
    description: 'Tarjeta de crédito o débito',
    icon: '💳',
    color: '#d32f2f',
    borderColor: '#d32f2f',
    backgroundColor: '#ffebee',
    // Logo oficial de Webpay Plus (descargado de transbankdevelopers.cl)
    logo: '/logos/webpay-plus.svg',
    features: [
      '✓ Todas las tarjetas',
      '✓ Pago inmediato',
      '✓ Seguro y certificado'
    ],
    securityInfo: `Webpay Plus: Procesado por Transbank, líder en medios de pago en Chile.
      Acepta todas las tarjetas de crédito y débito. Certificado y seguro.`,
    enabled: false,  // Se actualiza dinámicamente desde el backend
    notes: 'Método de pago oficial de Transbank. Totalmente integrado.'
  }
}

/**
 * Obtener métodos de pago habilitados
 */
export const getEnabledPaymentMethods = (): PaymentMethodConfig[] => {
  return Object.values(PAYMENT_METHODS).filter(method => method.enabled)
}

/**
 * Obtener métodos en desarrollo
 */
export const getPaymentMethodsInDevelopment = (): PaymentMethodConfig[] => {
  return Object.values(PAYMENT_METHODS).filter(method => method.inDevelopment)
}

/**
 * Habilitar/Deshabilitar método de pago
 */
export const setPaymentMethodEnabled = (methodId: PaymentMethodType, enabled: boolean): void => {
  if (PAYMENT_METHODS[methodId]) {
    PAYMENT_METHODS[methodId].enabled = enabled
  }
}

/**
 * Obtener configuración de método específico
 */
export const getPaymentMethodConfig = (methodId: PaymentMethodType): PaymentMethodConfig | undefined => {
  return PAYMENT_METHODS[methodId]
}

/**
 * Sincronizar configuración de métodos de pago con el backend
 * Consulta /api/payments/methods/config y actualiza PAYMENT_METHODS
 */
export const syncPaymentMethodsWithBackend = async (): Promise<void> => {
  try {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000'
    const response = await fetch(`${apiUrl}/api/payments/methods/config`)

    if (!response.ok) {
      console.error('Error fetching payment methods config:', response.statusText)
      return
    }

    const config = await response.json()

    // Actualizar configuración de cada método
    if (config.getnet !== undefined) {
      setPaymentMethodEnabled('getnet', config.getnet.enabled)
    }

    if (config.khipu !== undefined) {
      setPaymentMethodEnabled('khipu', config.khipu.enabled)
    }

    if (config.transbank !== undefined) {
      setPaymentMethodEnabled('transbank', config.transbank.enabled)
    }

    console.log('Payment methods synced with backend:', config)
  } catch (error) {
    console.error('Error syncing payment methods with backend:', error)
  }
}
